<script lang="ts">
  import type { GamePageStoryblok, GameStoryblok } from '$types/bloks';
  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import { currency, currentLocation } from '$lib/region';
  import { baseFormatCurrency, formatCurrency } from '$lib/utils/currency';
  import type { Game as PhaserGame } from 'phaser';
  import { onDestroy, onMount } from 'svelte';
  import GameSummary from './components/game-summary.svelte';
  import gameIntro from './assets/intro.mp4';
  import { fade } from 'svelte/transition';
  import {
    SHARED_GAME_SESSION_ID_QUERY_PARAM_NAME,
    createGameSession,
    getPublicGame
  } from './api/storage.client';
  import { region } from '$lib/region';
  import type { BaseGameSession, GameSession, PublicGameSession } from './api/types';
  import Badge from '$components/ui/badge.svelte';
  import { getDiscountAmount } from './api/score';
  import loadingScreenImage from './assets/loading-screen.png';
  import Icon from '$components/ui/icon/icon.svelte';
  import { PhaserEvents, SvelteEvents } from './events';
  import StartModal from './components/start-modal.svelte';
  import type { StoryblokStory } from 'storyblok-generate-ts';
  import { lang } from '$lib/lang';
  import { t } from '$lib/i18n';

  const GAME_MAX_WIDTH = 540;
  const GAME_MAX_HEIGHT = 982;
  const GAME_ASPECT_RATIO = GAME_MAX_WIDTH / GAME_MAX_HEIGHT;

  export let gameStory: StoryblokStory<GamePageStoryblok> | StoryblokStory<GameStoryblok>;
  export let closeGame: (() => void) | undefined = undefined;

  $: gameConfig = gameStory.content;

  let score = 0;
  let state: 'loading' | 'intro' | 'idle' | 'playing' | 'game-over' = 'loading';
  let audio = true;

  let gameContainerElement: HTMLElement;
  let phaserGame: PhaserGame | undefined;
  let sharedGameSession: PublicGameSession;
  let gameSession: BaseGameSession | GameSession | null = null;

  let initGamePromise: Promise<unknown>;

  const loadSharedGame = async () => {
    const sharedGameSessionId = $page.url.searchParams.get(SHARED_GAME_SESSION_ID_QUERY_PARAM_NAME);

    if (!sharedGameSessionId) {
      return;
    }

    sharedGameSession = await getPublicGame(sharedGameSessionId);
  };

  const startGame = async ({ reset }: { reset?: boolean } = {}) => {
    score = 0;
    state = 'playing';
    phaserGame?.scene.game.events.emit(reset ? SvelteEvents.RESET_GAME : SvelteEvents.START_GAME);

    gameSession = await createGameSession({
      gamePageId: gameStory.id.toString(),
      storeRegion: $region
    });
  };

  const endGame = async () => {
    phaserGame?.scene.game.events.emit(SvelteEvents.END_GAME);

    state = 'game-over';
  };

  const initGame = async () => {
    const { createGame } = await import('./game');

    // eslint-disable-next-line no-console
    console.log('Game is initializing...');

    phaserGame = await createGame({
      parentElement: gameContainerElement,
      debugMode: !!gameConfig.debug_mode
    });

    phaserGame.events.on(PhaserEvents.LOAD, () => {
      state = gameConfig.should_play_intro ? 'intro' : 'idle';
    });

    phaserGame.events.on(PhaserEvents.COIN_COLLECTED, () => {
      score += 1;
    });

    phaserGame.events.on(PhaserEvents.PLATFORM_SURPASSED, () => {
      score += 1;
    });

    phaserGame.events.on(PhaserEvents.COLLISION, () => {
      endGame();
    });
  };

  onMount(() => {
    if (!browser) {
      return;
    }

    initGamePromise = initGame();

    loadSharedGame();
  });

  onDestroy(async () => {
    await initGamePromise;

    if (phaserGame) {
      phaserGame.pause();
      phaserGame.destroy(true);
    }
  });

  let windowWidth = 0;
  let windowHeight = 0;

  const getGameDimensions = (sw: number, sh: number) => {
    const screenAspectRatio = sw / sh;
    const screenWidth = Math.min(GAME_MAX_WIDTH, sw - 32);
    const screenHeight = Math.min(GAME_MAX_HEIGHT, sh - 100);

    if (screenAspectRatio > GAME_ASPECT_RATIO) {
      return {
        width: (screenHeight * GAME_MAX_WIDTH) / GAME_MAX_HEIGHT,
        height: screenHeight
      };
    } else if (screenAspectRatio < GAME_ASPECT_RATIO) {
      return {
        width: screenWidth,
        height: (screenWidth * GAME_MAX_HEIGHT) / GAME_MAX_WIDTH
      };
    } else {
      return {
        width: screenWidth,
        height: screenHeight
      };
    }
  };

  $: dimensions = getGameDimensions(windowWidth, windowHeight);
</script>

<svelte:window bind:innerWidth={windowWidth} bind:innerHeight={windowHeight} />

<div
  style="width: {dimensions.width}px; height: {dimensions.height}px;"
  class="relative mx-auto rounded-md overflow-hidden z-[60]"
>
  <div
    id="game-container"
    class="h-full w-full bg-transparent"
    bind:this={gameContainerElement}
    style="opacity: {state === 'idle' ? '1' : '0'}); transition: opacity 2s linear;"
  />

  {#if state === 'loading'}
    <img
      alt=""
      aria-hidden="true"
      class="absolute inset-0 pointer-events-none h-full w-full object-contain"
      src={loadingScreenImage}
    />
    <p
      role="status"
      aria-live="polite"
      class="absolute font-primary text-2xl font-bold text-highlight bottom-40 left-1/2 -translate-x-1/2 text-center"
    >
      {$t('game.loading')}
    </p>
  {:else if state === 'intro'}
    <video
      class="absolute inset-0 z-10 pointer-events-none rounded-md"
      on:timeupdate={(e) => {
        if (e.currentTarget.currentTime >= e.currentTarget.duration - 0.5) {
          state = 'idle';
        }
      }}
      out:fade={{ duration: 1000 }}
      src={gameIntro + '#t=0.0001'}
      muted
      playsinline
      autoplay
    />
  {:else}
    {#if state === 'idle'}
      <StartModal {sharedGameSession} on:start={() => startGame()} />
    {/if}
    <!-- Mute audio buttons  -->
    {#if state === 'idle' || state === 'game-over'}
      <button
        name="Toggle audio"
        aria-label="Toggle audio"
        class="absolute left-3 top-3 h-6 w-6 flex items-center justify-center"
        on:click={() => {
          audio = !audio;
          phaserGame?.scene.game.events.emit(SvelteEvents.SET_AUDIO, audio);
        }}
      >
        {#if audio}
          <Icon icon="audio" />
        {:else}
          <Icon icon="audio-muted" />
        {/if}
      </button>
    {/if}
    <button
      name="Close game"
      aria-label="Close game"
      class="absolute right-3 top-3 h-6 w-6 flex items-center justify-center"
      on:click={closeGame}
    >
      <Icon icon="cross" />
    </button>
    {#if state === 'playing'}
      <div class="absolute inset-0 p-2 h-full pointer-events-none">
        <div class="flex items-center gap-2">
          <Badge size="md" class="z-10">Score: {score}</Badge>
          <Badge size="md" class="z-10" icon="discount">
            {#if $currentLocation == 'au' || $currentLocation == 'ca'}
              {baseFormatCurrency(
                `${$lang}-${$currentLocation}`,
                $currency,
                getDiscountAmount(score)
              )}
            {:else}
              {$formatCurrency($currency, getDiscountAmount(score))}
            {/if}
          </Badge>
        </div>
      </div>
    {/if}

    {#if state === 'game-over'}
      <GameSummary
        {score}
        {gameSession}
        on:restart={() => startGame({ reset: true })}
        sharePagePath={gameStory.full_slug}
      />
    {/if}
  {/if}
</div>
